import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { WMSFetch } from '../../utils/common-api-calls';
import { useAuth } from '../../context/wms-context';
import { defaultCommentData, QUOTE_LINE_SELECTED_STATES, aeroxchangeSource, skyselectSource, QUOTE_TYPES } from './data';
import { hasValidInventoryPriceForType } from './functions';
import SecondaryInventoryRow from '../../components/sms/SecondaryInventoryRow';
import InventoryLineRow from '../../components/sms/InventoryLineRow';
import QuoteLineRow from '../../components/sms/QuoteLineRow';
import Notification from '../../../components/Notification';
import StockCheckModal from '../../components/sms/StockCheckModal';
import CommentEditModal from '../../components/sms/CommentEditModal';
import CommitModal from '../../components/sms/CommitModal';
import ScrapePartButton from '../../components/sms/ScrapePartButton';
import FirstStaticBottomRow from '../../components/sms/FirstStaticBottomRow';
import SecondBottomStaticRow from '../../components/sms/SecondBottomStaticRow';
import SelectAllQuoteLinesButton from '../../components/sms/SelectAllQuoteLinesButton';
import BackButton from '../../components/sms/BackButton';
import RFQSubmissionModal from '../../components/sms/RFQSubmissionModal';
import ThirdBottomStaticRow from '../../components/sms/ThirdBottomStaticRow';

export default function WMS_QuoteSubmission({ quoteLineIds, partId, partName, setActivePage, setAutoQuoteData }) {
    const { currentUser } = useAuth();

    const [data, setData] = useState(null);

    const [loading, setLoading] = useState(false);
    const [loadingCommit, setLoadingCommit] = useState(false);
    const [loadingScrapeSubmit, setLoadingScrapeSubmit] = useState(false);
    const [loadingPricingHistory, setLoadingPricingHistory] = useState(false);

    const [showNotification, setShowNotification] = useState({ shown: false, status: null, title: '', message: '' });

    const [selectedQuoteLines, setSelectedQuoteLines] = useState([]);
    const [selectedInventory, setSelectedInventory] = useState([]);

    const [maxQuantity, setMaxQuantity] = useState(0);
    const [pickedQuantity, setPickedQuantity] = useState(0);
    const [selectedAllInventory, setSelectedAllInventory] = useState(false);

    const [conditionFilter, setConditionFilter] = useState('');

    const [stockCheckData, setStockCheckData] = useState(null);
    const [commentData, setCommentData] = useState(defaultCommentData);
    const [commitModalOpen, setCommitModalOpen] = useState(false);

    const [rfqModalOpen, setRFQModalOpen] = useState(false);

    const [aexSubmission, setAexSubmission] = useState(false);

    const hasPortalTypeSelected = data?.quotes?.some((quote) => quote.source === 'Portal' && selectedQuoteLines.includes(quote.quoteLineNumber));

    /**
     * Functionality to determine when the "Commit" button can be selected
     * @returns
     */
    const handleDisableCommitButton = () => {
        const selectedQuoteLineData = data?.quotes?.filter((quote) => selectedQuoteLines.includes(quote.quoteLineNumber));
        const selectedInventoryLineData = data?.inventoryLines?.filter((line) =>
            selectedInventory.includes(line.inventoryName)
        );
        const everyLineIsIgnoreOrNQ = selectedQuoteLineData?.every(
            (line) =>
                line.selectedState === QUOTE_LINE_SELECTED_STATES.NO_QUOTE ||
                line.selectedState === QUOTE_LINE_SELECTED_STATES.IGNORE
        );
        const isEveryLineBroker = selectedQuoteLineData?.every((line) => line.customerType === 'Broker');
        const isEveryLineAirlineOrMRO = selectedQuoteLineData?.every(
            (line) => line?.type === 'Airline' && line?.type === 'MRO'
        );
        const hasSelectedInventoryPrice = selectedInventoryLineData?.every(hasValidInventoryPriceForType);
        const hasSelectedInventoryBrokerPrice = selectedInventoryLineData?.every((line) => line.brokerPrice);

        // Disable button if selected quotes are Aeroxchange or SkySelect and inventory is of type exchange
        const isSomeLineAeroxchangeOrSkySelect = selectedQuoteLineData?.some(
            (line) => line.source === aeroxchangeSource || line.source === skyselectSource
        );
        const hasSelectedInventoryExchange = selectedInventoryLineData?.some(
            (inventory) => inventory.type === 'Exchange'
        );
        if (isSomeLineAeroxchangeOrSkySelect && hasSelectedInventoryExchange) return true;

        // Must have quotes selected
        // If the quotes selected are NQ or Ignore the button can be clicked
        if (!selectedQuoteLines?.length) return true;
        if (selectedQuoteLines?.length && everyLineIsIgnoreOrNQ) return false;

        // Selected quotes must have selected inventory
        if (selectedQuoteLines?.length && !selectedInventory?.length) return true;

        // Out of the inventory selected if there is no pricing then you cannot commit
        if (!hasSelectedInventoryPrice && !hasSelectedInventoryBrokerPrice) return true;

        // Inventory selected must have prices for the quote type specified
        if (isEveryLineBroker && hasSelectedInventoryPrice) return false;
        if (isEveryLineAirlineOrMRO && hasSelectedInventoryBrokerPrice) return false;

        return false;
    };
    const commitButtonDisabled = handleDisableCommitButton();

    const closeNotification = () => {
        setShowNotification({ shown: false, status: null, title: '', message: '' });
    };

    /**
     * Add all inventory lines and their data to selectedInventory
     */
    const selectAllInventoryLines = () => {
        let quantity = 0;
        const inventoryLinesWithPrice = data?.inventoryLines?.filter(hasValidInventoryPriceForType);
        const allInventoryLineNames = inventoryLinesWithPrice.map((inv) => {
            quantity += inv.quantity;
            return inv.inventoryName;
        });
        setPickedQuantity(quantity);
        setSelectedInventory(allInventoryLineNames);
        setSelectedAllInventory(true);
    };

    const deselectAllInventoryLines = () => {
        setSelectedInventory([]);
        setPickedQuantity(0);
        setSelectedAllInventory(false);
    };

    /**
     * Add or remove inventory selection along with data
     * @param {*} event On click action sent by checkbox
     * @param {*} inventoryLineName Name of the inventory/secondary inventory selected
     * @param {*} quantity Quantity on the inventory selected
     * @returns
     */
    const updateSelectedInventory = (event, inventoryLineName, quantity) => {
        const checked = event.target.checked;

        if (checked) {
            // Handle for duel purpose of adding inventory as well as updating selected trace
            setSelectedInventory((prev) => [...prev, inventoryLineName]);
            setPickedQuantity((pickedQty) => pickedQty + quantity);
        }
        if (!checked) {
            setSelectedInventory((prev) => prev.filter((inv) => inv !== inventoryLineName));
            setPickedQuantity((pickedQty) => pickedQty - quantity);
        }
    };

    /**
     * Used to change selected state of a quote line
     * @param {*} event
     * @param {*} quoteLineName
     * @param {*} state
     */
    const updateSelectedQuoteLines = async (event, quoteLineName, state, autoId) => {
        try {
            const checked = event.target.checked;
            const quoteBeforeChange = data?.quotes?.find((quote) => quote.quoteLineNumber === quoteLineName);
            const stateBeforeChange = quoteBeforeChange?.selectedState;

            if (checked) {
                setSelectedQuoteLines((prev) => [...prev, quoteLineName]);
                if (state) {
                    const updatedQuoteLines = data?.quotes?.map((quote) => {
                        if (quote.quoteLineNumber === quoteLineName) {
                            return {
                                ...quote,
                                email: currentUser.authentication.userEmail,
                                selectedState: state,
                                currentStep: null,
                            };
                        }
                        return quote;
                    });
                    setData((prevData) => {
                        return {
                            ...prevData,
                            quotes: updatedQuoteLines,
                        };
                    });
                    if (stateBeforeChange === QUOTE_LINE_SELECTED_STATES.LOCKED) {
                        await updateLockedStatus(autoId, false);
                    } else if (state === QUOTE_LINE_SELECTED_STATES.LOCKED) {
                        await updateLockedStatus(autoId, true);
                    }
                }
            }
            if (!checked) {
                if (state) {
                    const updatedQuoteLines = data?.quotes?.map((quote) => {
                        if (quote.quoteLineNumber === quoteLineName) {
                            return { ...quote, email: null, selectedState: null };
                        }
                        return quote;
                    });
                    setData((prevData) => {
                        return {
                            ...prevData,
                            quotes: updatedQuoteLines,
                        };
                    });
                    if (state === QUOTE_LINE_SELECTED_STATES.LOCKED) {
                        await updateLockedStatus(autoId, false);
                    }
                }
                setSelectedQuoteLines((prev) => prev.filter((inv) => inv !== quoteLineName));
            }
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error updating quote status',
                message:
                    'An error has occured updating the status for the selected quote. If the error persists please contact your manager.',
            });
        }
    };

    /**
     * Initial fetch to get quote line data
     * Includes initial hero photo update for all inventory
     */
    const getQuoteLineData = async () => {
        try {
            setLoading(true);
            const body = {
                quoteLineIds,
                partId,
                email: currentUser.authentication.userEmail,
                userName: currentUser.authentication.userName,
            };
            const quoteSpecificData = await WMSFetch('wms/sms/get-quote-specific-data', body, currentUser);
            if (!quoteSpecificData || quoteSpecificData?.error) throw Error();

            const sortedQuoteLines = quoteSpecificData.quotes.sort((a, b) => b.quantity - a.quantity);
            setMaxQuantity(sortedQuoteLines?.[0]?.quantity);

            const defaultQuotes = quoteSpecificData?.quotes?.map((quote) => {
                if (quote?.currentStep) return quote;
                if (quote?.selectedState === QUOTE_LINE_SELECTED_STATES.LOCKED) return { ...quote, currentStep: null };
                return {
                    ...quote,
                    selectedState: QUOTE_LINE_SELECTED_STATES.SELECT,
                };
            });
            setData({
                ...quoteSpecificData,
                quotes: defaultQuotes,
            });
            setSelectedQuoteLines(defaultQuotes.map((quote) => quote.quoteLineNumber));
            // await updateHeroPhotosForAllInventory(quoteSpecificData.inventoryLines);
            await getRelatedPricingData(partId, defaultQuotes, quoteSpecificData?.inventoryLines);
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error retrieving data for selected quotes',
                message:
                    'An error has occured getting information for the selected quotes. If the error persists please contact your manager.',
            });
        } finally {
            setLoading(false);
        }
    };

    const getRelatedPricingData = async (productId, quoteLines, inventoryLines = null) => {
        try {
            setLoadingPricingHistory(true);
            const body = {
                partId: productId,
                quoteLines,
            };

            const relatedPricingData = await WMSFetch('wms/sms/get-related-pricing-history', body, currentUser);
            if (!relatedPricingData || relatedPricingData?.error) throw Error();

            // Update inventory lines with matching CCP Price since we are fetching per part
            // Inventory lines stay static but CCP will change per selected part assuming alternates exist
            const inventoryLinesToUpdate = data?.inventoryLines ?? inventoryLines;
            const updatedInventoryLineData = inventoryLinesToUpdate?.map((inventoryLine) => {
                if (!relatedPricingData.ccp?.length) return inventoryLine;

                // Exchange CCP is applied when the user switches Quote Type to Exchange/Both in the modal.
                const matchingCCPOutright = relatedPricingData.ccp.find(
                    (ccpData) =>
                        ccpData.conditionRating === inventoryLine.conditionRating &&
                        inventoryLine.partId === ccpData.productId &&
                        (ccpData.saleType === QUOTE_TYPES.OUTRIGHT || ccpData.saleType === null)
                );

                if (!matchingCCPOutright) return inventoryLine;

                return {
                    ...inventoryLine,
                    outrightPrice: matchingCCPOutright.price,
                };
            });

            const updatedData = {
                ...data,
                ...relatedPricingData,
                inventoryLines: updatedInventoryLineData,
            };
            setData(updatedData);
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error retrieving pricing history',
                message:
                    'An error has occured getting related pricing data. If the error persists please contact your manager.',
            });
        } finally {
            setLoadingPricingHistory(false);
        }
    };

    /**
     * Updates quote line externalComments with an override called updatedComments. Runs on "Comments" input change
     * @param {*} id
     * @param {*} comments
     */
    const updateQuoteLineComments = (id, comments) => {
        setData((prevData) => {
            const updated = {
                ...prevData,
                quotes: prevData.quotes.map((quote) =>
                    quote.quoteLineId === id ? { ...quote, updatedComments: comments } : quote
                ),
            };
            return updated;
        });
        setCommentData(defaultCommentData);
    };

    /**
     * Set immediate LOCK status for a quote line
     * @param {*} autoId
     * @param {*} isChecked
     */
    const updateLockedStatus = async (autoId, isChecked) => {
        try {
            const body = {
                autoId,
                isLocked: isChecked,
            };
            const response = await WMSFetch('wms/sms/toggle-lock-status', body, currentUser);
            if (response?.error) {
                throw Error(response.Error);
            }
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error updating lock status',
                message:
                    'An error has occured updating the lock status for the selected quote. If the error persists please contact your manager.',
            });
        }
    };

    const preventNavigation = (e) => {
        e.preventDefault();
        e.returnValue = '';
    };

    const handleHeroCountChange = (inventoryName, newHeroCount) => {
        setData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                inventoryLines: prev.inventoryLines.map((l) =>
                    l.inventoryName === inventoryName ? { ...l, heroPhotoCount: newHeroCount } : l
                ),
            };
        });
    };

    useEffect(() => {
        // When more than one inventory line is selected deselect any currently selected Aeroxchange quote lines.
        if (selectedInventory?.length > 1) {
            const quoteLinesFromAeroxchange = data?.quotes?.filter(
                (quote) => selectedQuoteLines.includes(quote.quoteLineNumber) && quote.source === aeroxchangeSource
            );
            if (quoteLinesFromAeroxchange?.length) {
                const selectedQuoteNumbersFromAeroxchange = quoteLinesFromAeroxchange.map((quote) => quote.quoteLineNumber);
                setSelectedQuoteLines((prev) =>
                    prev.filter((quoteLineNumber) => !selectedQuoteNumbersFromAeroxchange.includes(quoteLineNumber))
                );
            }
        }
    }, [selectedInventory]);

    useEffect(() => {
        getQuoteLineData();

        window.history.pushState(null, document.title, window.location.href);
        window.addEventListener('popstate', function () {
            window.history.pushState(null, document.title, window.location.href);
        });

        window.addEventListener('beforeunload', preventNavigation);
        return () => {
            window.removeEventListener('beforeunload', preventNavigation);
        };
    }, []);

    useEffect(() => {
        if (showNotification.shown) {
            setTimeout(() => {
                closeNotification();
            }, 5_000);
        }
    }, [showNotification]);

    if (loading || !data) {
        return (
            <div className='flex flex-col h-[calc(100vh-64px)] items-center justify-center'>
                <div className='h-28 w-28 border-b-[5px] border-e-[5px] mb-7 border-gray-500 rounded-full animate-spin out' />
                <p className='text-xl'>Retrieving Data for Quote Lines</p>
            </div>
        );
    }

    const borderClasses = 'border-r-[1px] border-white px-1';

    const getDynamicConditionCodes = () => {
        if (!data?.inventoryLines) return ['All'];

        const conditionSet = new Set();
        let hasNull = false;

        data.inventoryLines.forEach((line) => {
            const condition = line.condition?.toString().trim().toUpperCase();
            if (!condition || condition === 'NULL') {
                hasNull = true;
            } else {
                conditionSet.add(condition);
            }
        });

        const sortedConditions = Array.from(conditionSet).sort((a, b) => a.localeCompare(b));
        const conditions = hasNull ? ['NULL', ...sortedConditions] : sortedConditions;
        return ['All', ...conditions];
    };

    return (
        <>
            <Notification
                showSavedNotification={showNotification.shown}
                setShowSavedNotification={closeNotification}
                status={showNotification.status}
                title={showNotification.title}
                desc={showNotification.message}
            />
            <StockCheckModal
                stockCheckData={stockCheckData}
                setStockCheckData={setStockCheckData}
                setShowNotification={setShowNotification}
                currentUser={currentUser}
                setData={setData}
            />
            <CommentEditModal
                commentData={commentData}
                setCommentData={setCommentData}
                updateQuoteLineComments={updateQuoteLineComments}
            />
            <CommitModal
                isOpen={commitModalOpen}
                setIsOpen={setCommitModalOpen}
                currentUser={currentUser}
                setShowNotification={setShowNotification}
                setLoadingCommit={setLoadingCommit}
                setSelectedQuoteLines={setSelectedQuoteLines}
                data={data}
                setData={setData}
                selectedQuoteLines={selectedQuoteLines}
                selectedInventory={selectedInventory}
                loadingCommit={loadingCommit}
                aexSubmission={aexSubmission}
                setAexSubmission={setAexSubmission}
            />
            <RFQSubmissionModal
                open={rfqModalOpen}
                setOpen={setRFQModalOpen}
                currentUser={currentUser}
                setShowNotification={setShowNotification}
                partId={partId}
                getRelatedPricingData={() => getRelatedPricingData(partId, data.quotes)}
                partName={partName}
            />
            <section className='px-8 pt-8 pb-10 w-full'>
                <header className='flex flex-row justify-between pb-4 text-lg font-bold text-blue-primary'>
                    <h1>Inventory Selection</h1>
                    <h1>
                        Max Qty: {maxQuantity} || Picked Qty: {pickedQuantity}
                    </h1>
                    <div className='flex flex-row gap-x-2 justify-center items-center'>
                        <h1>
                            Part: {data.partName} || Keyword: {data.partKeyword}
                        </h1>
                    </div>
                </header>
                <article className='flex gap-x-4 justify-start items-center w-full'>
                    <BackButton
                        data={data}
                        currentUser={currentUser}
                        setAutoQuoteData={setAutoQuoteData}
                        setData={setData}
                        setActivePage={setActivePage}
                        setShowNotification={setShowNotification}
                    />
                    <button
                        className='px-4 py-2 text-white rounded-md bg-blue-primary'
                        onClick={() => setRFQModalOpen(true)}
                    >
                        Create RFQ
                    </button>
                    <ScrapePartButton
                        loadingScrapeSubmit={loadingScrapeSubmit}
                        setLoadingScrapeSubmit={setLoadingScrapeSubmit}
                        setShowNotification={setShowNotification}
                        currentUser={currentUser}
                        partName={data.partName}
                    />
                    <h1 className='text-lg font-bold text-blue-primary'>Last Scrape Date: {data.lastScrapeDate || 'N/A'}</h1>
                </article>
                <section className='flex flex-col gap-y-4 pt-4 pb-8'>
                    {data?.quotes?.filter((quote) => quote.source === aeroxchangeSource)?.length ? (
                        <div className='w-full h-full'>
                            <article className='w-full border overflow-auto max-h-[400px]'>
                                <table className='w-full'>
                                    <thead className='sticky top-0 z-20 text-white bg-blue-primary'>
                                        <tr className='text-center'>
                                            <th className={borderClasses}>Customer</th>
                                            <th className={borderClasses}>Type</th>
                                            <th className={borderClasses}>Quote Line Number</th>
                                            <th className={borderClasses}>Quote Number</th>
                                            <th className={borderClasses}>Created Date</th>
                                            <th className={borderClasses}>Source</th>
                                            <th className={borderClasses}>Status</th>
                                            <th className={borderClasses}>Quantity</th>
                                            <th className={borderClasses}>Condition</th>
                                            <th className={borderClasses}>Priority</th>
                                            <th className={borderClasses}>Comments</th>
                                            <th className={borderClasses}>Lock</th>
                                            {/* <th>Ignore</th> */}
                                            <th className={borderClasses}>NQ</th>
                                            <th className={borderClasses}>Selected</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.quotes
                                            ?.filter((quote) => quote.source === aeroxchangeSource)
                                            .map((data, index) => {
                                                return (
                                                    <QuoteLineRow
                                                        key={index}
                                                        quoteLineData={data}
                                                        selectedQuoteLines={selectedQuoteLines}
                                                        selectedInventory={selectedInventory}
                                                        email={currentUser.authentication.userEmail}
                                                        updateQuoteLineComments={updateQuoteLineComments}
                                                        setCommentData={setCommentData}
                                                        updateSelectedQuoteLines={updateSelectedQuoteLines}
                                                    />
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </article>
                            <section className='flex flex-row items-center mt-4 w-full'>
                                <h1>
                                    Links will be validated before sending as some customers require them and there may not
                                    be valid links. The documents that are sent are not determined by the selected documents
                                    below, they follow the standard AQ rules for AEX documents. No pictures are sent to AEX
                                    orders.
                                </h1>
                                <article className='flex flex-row gap-x-4 justify-end items-end pl-4 w-1/4'>
                                    <button
                                        disabled={
                                            commitButtonDisabled ||
                                            selectedInventory.length > 1 ||
                                            !data?.quotes?.some(
                                                (quote) =>
                                                    selectedQuoteLines.includes(quote.quoteLineNumber) &&
                                                    quote.source === aeroxchangeSource
                                            )
                                        }
                                        className='px-4 py-2 text-white rounded-md bg-blue-primary'
                                        onClick={() => {
                                            setAexSubmission(true);
                                            setCommitModalOpen(true);
                                        }}
                                    >
                                        {loadingCommit ? 'Sending...' : 'Send AEX Quote'}
                                    </button>
                                </article>
                            </section>
                        </div>
                    ) : null}
                    {data?.quotes?.filter((quote) => quote.source !== aeroxchangeSource)?.length ? (
                        <div className='w-full h-full'>
                            <article className='w-full border overflow-auto max-h-[400px]'>
                                <table className='w-full'>
                                    <thead className='sticky top-0 z-20 text-white bg-blue-primary'>
                                        <tr className='text-center'>
                                            <th className={borderClasses}>Customer</th>
                                            <th className={borderClasses}>Type</th>
                                            <th className={borderClasses}>Quote Line Number</th>
                                            <th className={borderClasses}>Quote Number</th>
                                            <th className={borderClasses}>Created Date</th>
                                            <th className={borderClasses}>Source</th>
                                            <th className={borderClasses}>Status</th>
                                            <th className={borderClasses}>Quantity</th>
                                            <th className={borderClasses}>Condition</th>
                                            <th className={borderClasses}>Priority</th>
                                            <th className={borderClasses}>Comments</th>
                                            <th className={borderClasses}>Lock</th>
                                            {/* <th>Ignore</th> */}
                                            <th className={borderClasses}>NQ</th>
                                            <th className={borderClasses}>Selected</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.quotes
                                            ?.filter((quote) => quote.source !== aeroxchangeSource)
                                            .map((data, index) => {
                                                return (
                                                    <QuoteLineRow
                                                        key={index}
                                                        quoteLineData={data}
                                                        selectedQuoteLines={selectedQuoteLines}
                                                        selectedInventory={selectedInventory}
                                                        email={currentUser.authentication.userEmail}
                                                        updateQuoteLineComments={updateQuoteLineComments}
                                                        setCommentData={setCommentData}
                                                        updateSelectedQuoteLines={updateSelectedQuoteLines}
                                                    />
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </article>
                        </div>
                    ) : null}
                    <section className='flex flex-row items-center w-full'>
                        <section className='flex justify-between pr-4 mt-2 w-full'>
                            <div className='flex gap-x-2 items-center px-3 py-2 bg-white'>
                                <label className='text-sm font-semibold text-gray-700 whitespace-nowrap'>Condition:</label>
                                <select
                                    className='h-[36px] border border-gray-300 rounded px-2 text-sm text-black bg-white focus:outline-none focus:ring-1 focus:ring-blue-500'
                                    value={conditionFilter}
                                    onChange={(e) => setConditionFilter(e.target.value)}
                                >
                                    {getDynamicConditionCodes().map((condition) => (
                                        <option
                                            key={condition}
                                            value={condition === 'All' ? '' : condition === 'NULL' ? '' : condition}
                                        >
                                            {condition}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </section>
                        <article className='flex flex-row gap-x-4 justify-end items-end pl-4 w-full'>
                            <SelectAllQuoteLinesButton
                                data={data}
                                currentUser={currentUser}
                                setData={setData}
                                setSelectedQuoteLines={setSelectedQuoteLines}
                                setShowNotification={setShowNotification}
                            />
                            {data?.inventoryLines?.length ? (
                                <button
                                    className='px-4 py-2 text-white whitespace-nowrap rounded-md bg-blue-primary'
                                    disabled={!data?.inventoryLines.some((inventory) => inventory.price)}
                                    onClick={() => {
                                        selectedAllInventory ? deselectAllInventoryLines() : selectAllInventoryLines();
                                    }}
                                >
                                    {selectedAllInventory ? 'Deselect All Inventory Lines' : 'Select All Inventory Lines'}
                                </button>
                            ) : null}
                            <button
                                disabled={
                                    commitButtonDisabled ||
                                    !data?.quotes?.some(
                                        (quote) =>
                                            selectedQuoteLines.includes(quote.quoteLineNumber) &&
                                            quote.source !== aeroxchangeSource
                                    )
                                }
                                className='px-4 py-2 text-white rounded-md bg-blue-primary'
                                onClick={() => setCommitModalOpen(true)}
                            >
                                {loadingCommit ? 'Sending...' : 'Send Quote'}
                            </button>
                        </article>
                    </section>
                    <div className='flex flex-row gap-x-5 h-6'>
                        {loadingPricingHistory ? (
                            <div className='flex justify-center items-center'>
                                <div className='h-6 w-6 border-b-[2px] border-e-[2px] border-gray-500 rounded-full animate-spin' />
                            </div>
                        ) : (
                            <input
                                className='w-8 scale-125'
                                type='checkbox'
                                checked={data.partName === partName}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        getRelatedPricingData(partId, data.quotes);
                                    }
                                }}
                            />
                        )}
                        <h1>
                            Primary: <strong>{partName}</strong>
                        </h1>
                    </div>
                    <article className='w-full border overflow-auto max-h-[400px]'>
                        <table className='w-full'>
                            <thead className='sticky top-0 z-20 text-white bg-blue-primary'>
                                <tr className='w-full text-center'>
                                    <th className={borderClasses}>INV</th>
                                    <th className={borderClasses}>CC</th>
                                    <th className={borderClasses}>
                                        Override
                                        <br />
                                        Condition
                                    </th>
                                    <th className={borderClasses}>OC</th>
                                    <th className={borderClasses}>Trace</th>
                                    <th className={borderClasses}>
                                        ACQ
                                        <br />
                                        Cost
                                    </th>
                                    <th className={borderClasses}>Warehouse</th>
                                    <th className={borderClasses}>Comments</th>
                                    <th className={borderClasses}>Tags</th>
                                    <th className={borderClasses}>
                                        Tag
                                        <br />
                                        Date
                                    </th>
                                    <th className={borderClasses}>
                                        Tag
                                        <br />
                                        Agency
                                    </th>
                                    <th className={borderClasses}>Photos</th>
                                    <th className={borderClasses}>
                                        Part
                                        <br />
                                        Record
                                    </th>
                                    <th className={borderClasses}>Tag</th>
                                    <th className={borderClasses}>Owner</th>
                                    <th className={borderClasses}>1-Click</th>
                                    <th className={borderClasses}>
                                        Lead
                                        <br />
                                        Time
                                    </th>
                                    <th className={borderClasses}>Type</th>
                                    <th className={borderClasses}>Price</th>
                                    <th className={borderClasses}>Avail.</th>
                                    <th className={borderClasses}>
                                        Stock
                                        <br />
                                        Check
                                    </th>
                                    <th className='px-1'>Selected</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.inventoryLines
                                    ?.filter((line) => conditionFilter === '' || line.condition === conditionFilter)
                                    .map((line) => {
                                        if (line.isAlternate) return null;

                                        return (
                                            <InventoryLineRow
                                                key={line.id}
                                                inventoryLineData={line}
                                                updateSelectedInventory={updateSelectedInventory}
                                                selectedInventory={selectedInventory}
                                                setData={setData}
                                                currentUser={currentUser}
                                                setStockCheckData={setStockCheckData}
                                                brokerMarkup={data.brokerMarkup}
                                                onHeroCountChange={handleHeroCountChange}
                                                setPickedQuantity={setPickedQuantity}
                                                setCommentData={setCommentData}
                                                setNotification={setShowNotification}
                                                ccp={data?.ccp}
                                                hasPortalTypeSelected={hasPortalTypeSelected}
                                            />
                                        );
                                    })}
                            </tbody>
                        </table>
                    </article>
                    {Object.values(_.groupBy(data.inventoryLines, 'ProductName')).map((alternateInventoryLines) => {
                        if (alternateInventoryLines.some((inventory) => inventory.ProductName === partName)) {
                            return null;
                        }
                        return (
                            <div className='flex flex-col gap-y-3 w-full'>
                                <div className='flex flex-row gap-x-5 h-6'>
                                    {loadingPricingHistory ? (
                                        <div className='flex justify-center items-center'>
                                            <div className='h-6 w-6 border-b-[2px] border-e-[2px] border-gray-500 rounded-full animate-spin' />
                                        </div>
                                    ) : (
                                        <input
                                            className='w-8 scale-125'
                                            type='checkbox'
                                            checked={data.partName === alternateInventoryLines[0].ProductName}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    getRelatedPricingData(alternateInventoryLines[0].partId, data.quotes);
                                                }
                                            }}
                                        />
                                    )}
                                    <h1>
                                        ALTERNATIVE: <strong>{alternateInventoryLines[0].ProductName}</strong>
                                    </h1>
                                </div>
                                <article className='w-full border overflow-auto max-h-[400px]'>
                                    <table className='relative w-full'>
                                        <thead className='sticky top-0 z-20 text-white bg-blue-primary'>
                                            <tr className='w-full text-center'>
                                                <th className={borderClasses}>INV</th>
                                                <th className={borderClasses}>CC</th>
                                                <th className={borderClasses}>
                                                    Override
                                                    <br />
                                                    Condition
                                                </th>
                                                <th className={borderClasses}>OC</th>
                                                <th className={borderClasses}>Trace</th>
                                                <th className={borderClasses}>
                                                    ACQ
                                                    <br />
                                                    Cost
                                                </th>
                                                <th className={borderClasses}>Warehouse</th>
                                                <th className={borderClasses}>Comments</th>
                                                <th className={borderClasses}>Tags</th>
                                                <th className={borderClasses}>
                                                    Tag
                                                    <br />
                                                    Date
                                                </th>
                                                <th className={borderClasses}>
                                                    Tag
                                                    <br />
                                                    Agency
                                                </th>
                                                <th className={borderClasses}>Photos</th>
                                                <th className={borderClasses}>
                                                    Part
                                                    <br />
                                                    Record
                                                </th>
                                                <th className={borderClasses}>Tag</th>
                                                <th className={borderClasses}>Owner</th>
                                                <th className={borderClasses}>1-Click</th>
                                                <th className={borderClasses}>
                                                    Lead
                                                    <br />
                                                    Time
                                                </th>
                                                <th className={borderClasses}>Type</th>
                                                <th className={borderClasses}>Price</th>
                                                {/* <th className={borderStyles}>BPrice</th> */}
                                                <th className={borderClasses}>Avail.</th>
                                                <th className={borderClasses}>
                                                    Stock
                                                    <br />
                                                    Check
                                                </th>
                                                <th className='px-1'>Selected</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {alternateInventoryLines
                                                ?.filter(
                                                    (line) => conditionFilter === '' || line.condition === conditionFilter
                                                )
                                                .map((line) => (
                                                    <InventoryLineRow
                                                        key={line.id}
                                                        inventoryLineData={line}
                                                        updateSelectedInventory={updateSelectedInventory}
                                                        selectedInventory={selectedInventory}
                                                        setData={setData}
                                                        currentUser={currentUser}
                                                        setStockCheckData={setStockCheckData}
                                                        brokerMarkup={data.brokerMarkup}
                                                        onHeroCountChange={handleHeroCountChange}
                                                        setPickedQuantity={setPickedQuantity}
                                                        setCommentData={setCommentData}
                                                        setNotification={setShowNotification}
                                                        ccp={data?.ccp}
                                                    />
                                                ))}
                                        </tbody>
                                    </table>
                                </article>
                            </div>
                        );
                    })}
                    <article className='flex flex-row gap-x-4 w-full overflow-auto max-h-[400px]'>
                        <table className='relative w-full border'>
                            <thead className='sticky top-0 text-white bg-blue-primary'>
                                <tr className='text-center'>
                                    <th className={borderClasses}>Secondary Inventory</th>
                                    <th className={borderClasses}>Condition</th>
                                    <th className={'text-blue-primary ' + borderClasses}>Owner Code</th>
                                    <th className={borderClasses}>Trace</th>
                                    <th className='text-blue-primary'>Source</th>
                                    <th className='text-blue-primary'>Tags</th>
                                    <th className='text-blue-primary'>Pictures</th>
                                    <th className='text-blue-primary'>Part Record</th>
                                    <th className={'text-blue-primary ' + borderClasses}>Tag</th>
                                    <th className={borderClasses}>Internal Comments</th>
                                    <th className={borderClasses}>Customer</th>
                                    <th className={borderClasses}>Cost To Purchase</th>
                                    <th className={borderClasses}>Lead Time</th>
                                    <th className={borderClasses}>Price</th>
                                    {/* <th>BPrice</th> */}
                                    <th className={borderClasses}>Available</th>
                                    <th className='text-blue-primary border-r-[1px] border-white'>Stock</th>
                                    <th className='px-1'>Selected</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.secondaryInventory?.map((data, index) => {
                                    return (
                                        <SecondaryInventoryRow
                                            key={index}
                                            secondaryInventoryData={data}
                                            updateSelectedInventory={updateSelectedInventory}
                                            setData={setData}
                                            selectedInventory={selectedInventory}
                                            brokerMarkup={data.brokerMarkup}
                                        />
                                    );
                                })}
                            </tbody>
                        </table>
                    </article>
                </section>
                <article className='flex flex-col gap-y-2 justify-center items-center w-full'>
                    <h1 className='font-bold'>Internal Comments</h1>
                    <textarea
                        value={data.internalComments}
                        disabled={true}
                        className='rounded-md border px-3 py-2 resize-none h-[200px] w-full'
                    />
                </article>
                <FirstStaticBottomRow data={data} borderClasses={borderClasses} />
                <SecondBottomStaticRow data={data} borderClasses={borderClasses} />
                <ThirdBottomStaticRow data={data} borderClasses={borderClasses} />
                <section className='flex flex-col gap-y-1 py-5'>
                    <h1>Key*</h1>
                    <h1 className='px-1 py-1 bg-orange-500 bg-opacity-25 w-fit'>
                        Quote has multiple quote lines for same part
                    </h1>
                    <h1 className='px-1 py-1 bg-yellow-300 bg-opacity-40 w-fit'>
                        Quote Line Customer matches history record
                    </h1>
                    <h1 className='p-2 rounded-md border-4 border-green-600 w-fit'>Airline</h1>
                </section>
            </section>
        </>
    );
}
